import {
  MatDialogRef,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { EventEmitter, Component, Output, inject, OnInit } from '@angular/core';
import createDebug from 'debug';
import { TenantPersistedArray } from '../../business-objects/tenant-form-group';
import { AuthService } from '../../services/auth/auth.service';
import { TenantService } from '../../services/tenant/tenant.service';
import type { Id } from '../../base-types';
import { MatListItem, MatNavList } from '@angular/material/list';
import { AdElDirective } from '../../directives/ad-el/ad-el.directive';

const Debug = createDebug('TenantChooserDialogComponent');

/**
 * Tenant Chooser Dialog
 *
 * Dialog that lists the current user's tenants and lets them select one.
 * Administrators also get an "All Tenants" option, which clears the
 * selection so they work across every tenant. On selection, updates the
 * tenant service and emits the selected event (null for All Tenants).
 */

@Component({
  selector: 'tenant-chooser-dialog-content',
  templateUrl: './tenant-chooser-dialog.component.html',
  styleUrls: ['./tenant-chooser-dialog.component.scss'],
  imports: [
    AdElDirective,
    MatDialogContent,
    MatDialogTitle,
    MatListItem,
    MatNavList,
  ],
})
export class TenantChooserDialogContentComponent implements OnInit {
  constructor(
    public dialog: MatDialogRef<TenantChooserDialogContentComponent>,
  ) {}

  /** Tenants - The user's available tenants */ tenants: TenantPersistedArray =
    new TenantPersistedArray();

  /** Selected - Emitted when the user selects a tenant, or null for All Tenants */ @Output()
  selected: EventEmitter<any> = new EventEmitter<any>();

  /** Is Admin - Whether the user can work across all tenants */
  isAdmin = false;

  authService = inject(AuthService);

  tenantService = inject(TenantService);

  /** Initialize - Load the user's tenants */

  async initialize(): Promise<void> {
    const debug = Debug.extend('initialize');
    debug('loading tenants');
    this.isAdmin = await this.authService.hasRole('Administrator');
    debug('isAdmin %j', this.isAdmin);
    await this.tenants.read({ path: '/api/tenants/current-user' });
    debug('tenants.length %j', this.tenants.length);
  }

  /** Select All Tenants - Clear the active tenant so an administrator works across all tenants */
  selectAllTenants(): void {
    const debug = Debug.extend('selectAllTenants');
    debug('all tenants');
    this.tenantService.clearActiveTenant();
    this.selected.emit(null);
    this.dialog.close();
  }

  /** Select Tenant - Set the active tenant and close */
  selectTenant(tenantId: Id): void {
    const debug = Debug.extend('selectTenant');
    debug('tenantId %j', tenantId);
    this.tenantService.setActiveTenant(tenantId);
    this.selected.emit(tenantId);
    this.dialog.close();
  }

  ngOnInit(): void {
    this.initialize();
  }
}
